import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/useToast";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Skeleton from "@mui/material/Skeleton";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { CalendarMonth, Cancel, CheckCircle, ContentCut, Person } from "@mui/icons-material";
import ThemeToggle from "@/components/ThemeToggle";
import { formatCents, getStatusChipColor } from "@/lib/utils";
import { dialogActionsSx } from "@/lib/dialogStyles";
import type { BookingWithDetails } from "@shared/schema";

function StackRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <Box sx={{ color: "text.secondary", pt: 0.25 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography fontWeight={500}>{value}</Typography>
      </Box>
    </Box>
  );
}

export default function ManageBooking() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: booking, isLoading, error } = useQuery<BookingWithDetails>({
    queryKey: ["/api/manage", token],
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/manage/${token}`, { action: "cancel" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/manage", token] });
      toast({ title: t("toasts.bookingCancelled") });
      setConfirmOpen(false);
    },
    onError: (err: Error) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Skeleton variant="rounded" width={400} height={200} />
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <AppBar position="sticky" color="default" elevation={1}>
          <Toolbar sx={{ maxWidth: 448, mx: "auto", width: "100%", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ContentCut color="primary" />
              <Typography fontWeight={600}>{t("common.bookflow")}</Typography>
            </Box>
            <ThemeToggle />
          </Toolbar>
        </AppBar>
        <Box sx={{ maxWidth: 448, mx: "auto", px: 2, py: 8, textAlign: "center" }}>
          <Cancel sx={{ fontSize: 48, color: "action.disabled", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t("manageBooking.notFoundTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("manageBooking.notFoundBody")}
          </Typography>
        </Box>
      </Box>
    );
  }

  const isCancelled = booking.status === "cancelled";
  const isPast = new Date(booking.endAt) < new Date();

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar sx={{ maxWidth: 448, mx: "auto", width: "100%", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ContentCut color="primary" />
            <Typography fontWeight={600}>{t("common.bookflow")}</Typography>
          </Box>
          <ThemeToggle />
        </Toolbar>
      </AppBar>
      <Box sx={{ maxWidth: 448, mx: "auto", px: 2, py: 4 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            {isCancelled ? (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: "error.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Cancel color="error" />
              </Box>
            ) : (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: "success.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircle color="success" />
              </Box>
            )}
          </Box>
          <Typography variant="h6" data-testid="text-manage-title">
            {isCancelled ? t("manageBooking.bookingCancelledTitle") : t("manageBooking.yourBooking")}
          </Typography>
          <Chip sx={{ mt: 1 }} size="small" label={booking.status} color={getStatusChipColor(booking.status)} />
        </Box>

        <Card>
          <CardContent>
            <StackRow icon={<ContentCut fontSize="small" />} label={t("manageBooking.service")} value={booking.service?.name || ""} />
            <Divider sx={{ my: 2 }} />
            <StackRow icon={<Person fontSize="small" />} label={t("common.stylist")} value={booking.staffMember?.displayName || ""} />
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <CalendarMonth fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("manageBooking.dateTime")}
                </Typography>
                <Typography>
                  {new Date(booking.startAt).toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(booking.startAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} -{" "}
                  {new Date(booking.endAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                {t("manageBooking.price")}
              </Typography>
              <Typography fontWeight={600}>{formatCents(booking.service?.priceCents || 0)}</Typography>
            </Box>
          </CardContent>
        </Card>

        {!isCancelled && !isPast && (
          <Button
            fullWidth
            color="error"
            variant="contained"
            sx={{ mt: 3 }}
            onClick={() => setConfirmOpen(true)}
            data-testid="button-cancel-manage-booking"
          >
            {t("manageBooking.cancelBooking")}
          </Button>
        )}
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t("manageBooking.confirmCancelTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("manageBooking.confirmCancelBody")}
          </Typography>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button variant="outlined" onClick={() => setConfirmOpen(false)}>
            {t("manageBooking.keepBooking")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
          >
            {t("manageBooking.yesCancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
